import { yupResolver } from '@hookform/resolvers/yup';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Divider from '@mui/material/Divider';
import Drawer, { drawerClasses } from '@mui/material/Drawer';
import Typography from '@mui/material/Typography';
import {
  AiReviewStatus,
  deleteV1CompaniesCompanyIdTicketsTicketIdImage,
  FileAttachableType,
  getV1CompaniesCompanyIdTicketsId,
  LoadState,
  LoadSummary_Update,
  ModelError_Response,
  OrderUnitOfMeasure,
  patchV1CompaniesCompanyIdLoadsId,
  patchV1CompaniesCompanyIdLoadsLoadIdSummary,
  patchV1CompaniesCompanyIdTicketsId,
  postV1CompaniesCompanyIdTickets,
  putV1CompaniesCompanyIdLoadsLoadIdApprove,
  putV1CompaniesCompanyIdLoadsLoadIdUnapprove,
  Ticket_Create,
  TicketSource,
  TicketState,
  Waypoint_Update_Destroyable,
} from '@treadinc/horizon-api-spec';
import { AxiosError, isAxiosError } from 'axios';
import { t } from 'i18next';
import { cloneDeep, isNil, omit } from 'lodash-es';
import { observer } from 'mobx-react-lite';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import ConfirmationDialog from '~components/Dialog/ConfirmationDialog';
import { SmallButton } from '~components/Order/ordersDispatchStyledComponents';
import FileAttachments, {
  fileAttachmentSchema,
} from '~formsShared/components/FileAttachments';
import useAiReview from '~hooks/useAiReview/useAiReview';
import {
  trackAiReviewRejected,
  trackLoadsApproved,
} from '~hooks/useAnalytics/trackApprovals';
import useEffectiveCompanyId from '~hooks/useEffectiveCompanyId/useEffectiveCompanyId';
import { Job } from '~hooks/useJob/models';
import { useJob } from '~hooks/useJob/useJob';
import { Load, LoadSummaryBasic } from '~hooks/useLoad/models';
import { Order } from '~hooks/useOrders/models';
import { useOrders } from '~hooks/useOrders/useOrders';
import { JobLoadSignature } from '~hooks/useSignatures/models';
import { Ticket, TicketMetadata } from '~hooks/useTickets/models';
import useTicketExportRefresh from '~hooks/useTickets/useTicketExportRefresh';
import { useTickets } from '~hooks/useTickets/useTickets';
import { TicketExportBlock } from '~pages/Approvals/ApprovalsComponents/TicketExport';
import connection from '~services/connectionModule';
import { useStores } from '~store';
import { alert, AlertTypes } from '~types/AlertTypes';
import { mergeDateTime } from '~utils/dateTime';
import fetchAllPages from '~utils/fetchAllPages';
import { getApprovalStatus } from '~utils/jobs/job-utils';
import { getChangedScaleTicketIds } from '~utils/tickets/scale-ticket-fields';
import { toNullableId } from '~utils/tickets/ticket-input';
import { canEditTicket } from '~utils/tickets/ticket-utils';
import { isViewerAccountCustomer } from '~utils/user/user-utils';

import { FlexColumn } from '../styled';
import { mapLoadSummaryErrorsToStampFields } from '../TicketForm/stampFields';
import TicketForm, { TicketFormHandler } from '../TicketForm/TicketForm';
import AiReviewSummary from './AiReviewSummary';
import { TicketDrawerBusyReason } from './constants';
import DigitalTicketView from './DigitalTicketView';
import DrawerFooter from './DrawerFooter';
import DrawerHeader from './DrawerHeader';
import JobSummaryPanel from './JobSummaryPanel';
import LoadApprovals from './LoadApprovals';
import TicketAttachments from './TicketAttachments';
import TicketDetailsCTAs from './TicketDetailsCTAs';
import TicketDrawerEmptyState from './TicketDrawerEmptyState';

const fileAttachmentsFormSchema = yup.object().shape({
  fileAttachments: fileAttachmentSchema,
});

type AvailableCTAs =
  | 'approve-load'
  | 'approve-load-and-next'
  | 'edit-ticket'
  | 'flag-ticket'
  | 'unapprove-load'
  | 'unflag-ticket';

interface TicketDrawerProps {
  isDrawerOpen?: boolean;
  emptyStateMessage?: string;
  job: Job;
  load?: Load;
  loadNumber?: number;
  nextUnavailableReason?: string;
  onClose: () => void;
  onDeleteTicketImage?: () => Promise<void>;
  onLoadUpdated?: (load: Load) => void;
  onShowNextTicketClick?: () => void;
  onShowPreviousTicketClick?: () => void;
  previousUnavailableReason?: string;
  refreshLoads: () => void;
  refreshTicket: (ticket: Ticket, isNewTicket?: boolean) => void;
  ticket?: Ticket;
}

function TicketDrawer({
  job,
  emptyStateMessage,
  isDrawerOpen,
  load: initialLoad,
  loadNumber,
  nextUnavailableReason,
  onClose,
  onLoadUpdated,
  onShowNextTicketClick,
  onShowPreviousTicketClick,
  previousUnavailableReason,
  refreshLoads,
  refreshTicket,
  ticket: initialTicket,
}: TicketDrawerProps) {
  const { toasterStore, userStore, ticketsStore } = useStores();
  const { dismissAiReview, undismissAiReview } = useAiReview();

  const isOpen = isDrawerOpen ?? Boolean(initialLoad);
  const effectiveCompanyId = useEffectiveCompanyId();
  const companyId = job.company?.id ?? effectiveCompanyId;

  const [ticket, setTicket] = useState<Ticket>();
  const [load, setLoad] = useState<Load>();
  const [order, setOrder] = useState<Order>();
  const [signatures, setSignatures] = useState<JobLoadSignature[]>([]);
  const [isShowingTicketForm, setIsShowingTicketForm] = useState(!initialTicket);
  const [isConfirmingDiscard, setIsConfirmingDiscard] = useState(false);
  const [busyReason, setBusyReason] = useState<TicketDrawerBusyReason>();
  const [ticketMetadata, setTicketMetadata] = useState<TicketMetadata>();
  const [isLoadingTicketMetadata, setIsLoadingTicketMetadata] = useState(false);
  const analysisTimeoutRef = useRef<NodeJS.Timeout>();
  const ticketMetadataRequestRef = useRef<string | null>(null);
  const orderRequestRef = useRef<string | null>(null);

  const ticketFormRef = useRef<TicketFormHandler>(null);
  const pendingActionRef = useRef<(() => void) | null>(null);

  const fileAttachmentsForm = useForm({
    resolver: yupResolver(fileAttachmentsFormSchema),
    mode: 'onSubmit',
    reValidateMode: 'onChange',
    defaultValues: {},
  });
  const {
    control: fileAttachmentsControl,
    formState: { errors: fileAttachmentsErrors },
  } = fileAttachmentsForm;

  const userPermissions = userStore.getPermissions();

  const { getSignaturesByLoadId } = useJob();
  const { getOrderById } = useOrders();
  const { getTicketMetadataById, updateTicketPotentialDuplicate } = useTickets();

  const hasTicket = Boolean(ticket?.id);
  const {
    isApprovable: isLoadApprovable,
    isUnapprovable: isLoadUnapprovable,
    isApproved: isLoadApproved,
  } = load
    ? getApprovalStatus(load, userPermissions, Boolean(job.order?.editable))
    : { isApprovable: false, isUnapprovable: false, isApproved: false };
  const isReadOnlyTicket = isViewerAccountCustomer(userStore, job.order?.account);
  const isLoadCanceled = load?.state === LoadState.CANCELED;
  // Without the load guard the no-loads state, which never has a ticket, collapses the
  // footer row to 0px and pushes Close under the tread agent dock.
  const isTicketFormVisible = Boolean(load) && isShowingTicketForm;
  const isTicketEditable =
    ticket &&
    !isReadOnlyTicket &&
    canEditTicket(ticket.state as TicketState) &&
    !isLoadApproved;
  const canCreateTicket = !ticket && !isReadOnlyTicket && userPermissions.canCreateTicket;
  const isAnalyzingTicket = Boolean(
    ticket?.id && ticketsStore.analyzingTickets.get(ticket.id),
  );

  // An AI pass on a duplicate ticket says nothing the duplicate warning does not already
  // say, so it stays hidden — but once dismissed the summary is the only place the
  // dismissal can be undone from, so it has to keep rendering.
  const aiReviewIsDismissed = Boolean(load?.aiReview?.dismissedAt);
  const canDismissAiReview = isLoadApprovable || isLoadApproved;
  const hidesAiReviewSummary =
    !aiReviewIsDismissed &&
    load?.aiReview?.status === AiReviewStatus.APPROVED &&
    Boolean(ticket?.isPotentialDuplicateFlagged);

  const availableCTAs = useMemo(() => {
    const ctas: AvailableCTAs[] = [];

    if (hasTicket) {
      if (isTicketEditable && userPermissions.canEditTicket) {
        ctas.push('edit-ticket');

        if (ticket.flagged) {
          ctas.push('unflag-ticket');
        } else {
          ctas.push('flag-ticket');
        }
      }
    }

    if (isLoadApprovable) {
      ctas.push('approve-load');

      if (onShowNextTicketClick) {
        ctas.push('approve-load-and-next');
      }
    }

    if (isLoadUnapprovable) {
      ctas.push('unapprove-load');
    }

    return ctas;
  }, [
    hasTicket,
    isLoadApprovable,
    isLoadUnapprovable,
    isTicketEditable,
    onShowNextTicketClick,
    ticket?.flagged,
    userPermissions.canEditTicket,
  ]);

  const resetTicketDrawer = useCallback(() => {
    setTicket(undefined);
    setLoad(undefined);
    setIsShowingTicketForm(false);
    setBusyReason(undefined);
  }, []);

  const performClose = useCallback(() => {
    onClose();
    resetTicketDrawer();
  }, [onClose, resetTicketDrawer]);

  // Guards the accidental / ambiguous exits from a dirty edit — backdrop click,
  // Escape, and header prev/next navigation — since load data can be financial.
  // The explicit controls (Cancel and ✕) are deliberate, so they discard directly.
  const guardDirtyExit = useCallback(
    (action: () => void) => {
      if (isShowingTicketForm && ticketFormRef.current?.getIsDirty()) {
        pendingActionRef.current = action;
        setIsConfirmingDiscard(true);
        return;
      }

      action();
    },
    [isShowingTicketForm],
  );

  // Backdrop and Escape route through the Drawer's onClose.
  const handleGuardedClose = useCallback(
    () => guardDirtyExit(performClose),
    [guardDirtyExit, performClose],
  );

  // Cancel just exits edit mode (back to the read view); ✕ closes the drawer.
  // Both are deliberate, so they discard directly without a prompt.
  const handleCancelEdit = useCallback(() => setIsShowingTicketForm(false), []);

  const handleShowNextTicket = useCallback(
    () => guardDirtyExit(() => onShowNextTicketClick?.()),
    [guardDirtyExit, onShowNextTicketClick],
  );

  const handleShowPreviousTicket = useCallback(
    () => guardDirtyExit(() => onShowPreviousTicketClick?.()),
    [guardDirtyExit, onShowPreviousTicketClick],
  );

  const confirmDiscard = useCallback(() => {
    pendingActionRef.current?.();
    pendingActionRef.current = null;
    setIsConfirmingDiscard(false);
  }, []);

  const cancelDiscard = useCallback(() => {
    pendingActionRef.current = null;
    setIsConfirmingDiscard(false);
  }, []);

  const handleDeleteTicketImage = useCallback(async () => {
    if (!ticket?.id) {
      return;
    }

    try {
      setBusyReason(TicketDrawerBusyReason.MANIPULATING_IMAGE);

      try {
        await deleteV1CompaniesCompanyIdTicketsTicketIdImage({
          path: { 'company-id': effectiveCompanyId, 'ticket-id': ticket.id },
        });
      } catch (error) {
        // A 404 means the image is already gone (stale drawer state or a double
        // delete), so the desired end state — no image — is already true. Swallow
        // it and fall through to refresh the ticket instead of surfacing a noisy
        // error toast (HORIZON-FE-2GQ / TRE-15571).
        const imageAlreadyDeleted = isAxiosError(error) && error.response?.status === 404;

        if (!imageAlreadyDeleted) {
          throw error;
        }
      }

      const response = await getV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': effectiveCompanyId, id: ticket.id },
      });
      const updatedTicket = Ticket.parse(response.data.data);

      refreshTicket(updatedTicket);
    } catch (error) {
      connection.handleRequestError(
        error,
        t('error_messages.tickets.failed_to_delete_ticket_image'),
      );
    } finally {
      setBusyReason(undefined);
    }
  }, [effectiveCompanyId, ticket?.id, refreshTicket]);

  const handleExportRefreshed = useCallback(
    (refreshedTicket: Ticket) => {
      setTicket(refreshedTicket);
      refreshTicket(refreshedTicket);
    },
    [refreshTicket],
  );
  const startExportRefresh = useTicketExportRefresh({
    companyId: effectiveCompanyId,
    currentTicketId: ticket?.id,
    onTicketRefreshed: handleExportRefreshed,
  });

  const handleSaveTicket = useCallback(async () => {
    const formData = await ticketFormRef.current?.onSubmit();

    if (formData) {
      setBusyReason(TicketDrawerBusyReason.SAVING_TICKET);

      const assembledDatetime = mergeDateTime(formData.date, formData.time);
      // Each stamp carries its own date so loads spanning midnight persist the
      // correct day; the ticket-level date is only a fallback.
      const assembledArrivedPickupDatetime = formData.arrivedPickupTime
        ? mergeDateTime(
            formData.arrivedPickupDate ?? formData.date,
            formData.arrivedPickupTime,
          )
        : null;
      const assembledArrivedDropoffDatetime = formData.arrivedDropoffTime
        ? mergeDateTime(
            formData.arrivedDropoffDate ?? formData.date,
            formData.arrivedDropoffTime,
          )
        : null;
      const assembledLoadedDatetime = formData.loadedTime
        ? mergeDateTime(formData.loadedDate ?? formData.date, formData.loadedTime)
        : null;
      const assembledUnloadedDatetime = formData.unloadedTime
        ? mergeDateTime(formData.unloadedDate ?? formData.date, formData.unloadedTime)
        : null;

      const uom = formData.quantityAndUnit.uom.value as OrderUnitOfMeasure;
      const loadsNotes = formData.loadsNotes;
      const phaseId = ticketFormRef.current?.getPhaseId();

      const changedScaleTicketIds = getChangedScaleTicketIds(formData, ticket);

      const body: Ticket_Create = {
        external_machine_id: toNullableId(changedScaleTicketIds.machineId),
        external_trailer_id: toNullableId(changedScaleTicketIds.trailerId),
        quantity: formData.quantityAndUnit.quantity,
        ticket_number: formData.ticketNumber,
        unit_of_measure: uom,
        job_id: load?.jobId,
        material_id: formData.material.value,
        load_id: load?.id,
        service_date: assembledDatetime,
        source: TicketSource.WEB,
      };

      const loadSummaryBody: LoadSummary_Update = {
        arrived_pickup_at: assembledArrivedPickupDatetime,
        arrived_dropoff_at: assembledArrivedDropoffDatetime,
        loaded_at: assembledLoadedDatetime,
        unloaded_at: assembledUnloadedDatetime,
      };

      try {
        if (load?.id) {
          // Update load notes, phase, and waypoint sites (when changed).
          const loadBody: {
            notes: string | null | undefined;
            phase_id?: string | null;
            waypoints?: Waypoint_Update_Destroyable[];
          } = { notes: loadsNotes };
          if (phaseId !== undefined) {
            loadBody.phase_id = phaseId;
          }
          const waypointsUpdate = ticketFormRef.current?.getWaypointsUpdate();
          if (waypointsUpdate?.length) {
            loadBody.waypoints = waypointsUpdate;
          }

          const newLoad = await patchV1CompaniesCompanyIdLoadsId({
            path: { 'company-id': effectiveCompanyId, id: load.id },
            body: loadBody,
          });

          const parsedNewLoad = Load.parse(newLoad.data.data);
          let newLoadSummary = load.loadSummary;

          // Update load summary only if it existed before
          if (load.loadSummary !== null) {
            try {
              const response = await patchV1CompaniesCompanyIdLoadsLoadIdSummary({
                path: { 'company-id': effectiveCompanyId, 'load-id': load.id },
                body: loadSummaryBody,
              });
              newLoadSummary = LoadSummaryBasic.parse(response.data.data);
            } catch (error) {
              // The backend validates user-edited timestamps; surface those 422s
              // inline on the stamp fields instead of a toast. Anything else
              // falls through to the shared handler below.
              const stampFieldErrors =
                isAxiosError(error) && error.response?.status === 422
                  ? mapLoadSummaryErrorsToStampFields(
                      (error as AxiosError<ModelError_Response>).response?.data.error
                        .errors ?? [],
                    )
                  : [];

              if (stampFieldErrors.length === 0) {
                throw error;
              }

              ticketFormRef.current?.applyStampFieldErrors(stampFieldErrors);

              return;
            }
          }

          const updatedLoad = cloneDeep(parsedNewLoad);
          updatedLoad.loadSummary = newLoadSummary;
          setLoad(updatedLoad);
          onLoadUpdated?.(updatedLoad);
        }

        if (ticket?.id) {
          const response = await patchV1CompaniesCompanyIdTicketsId({
            path: { 'company-id': effectiveCompanyId, id: ticket.id },
            body: {
              ...omit(body, 'job_id', 'source'),
              material_id: body.material_id ?? undefined,
              load_id: body.load_id ?? undefined,
              service_date: body.service_date ?? undefined,
            },
          });
          const updatedTicket = Ticket.parse(response.data.data);

          setTicket(updatedTicket);
          startExportRefresh(updatedTicket);
          setIsShowingTicketForm(false);
          toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
        } else {
          const response = await postV1CompaniesCompanyIdTickets({
            path: { 'company-id': effectiveCompanyId },
            body,
          });
          const newTicket = Ticket.parse(response.data.data);

          setTicket(newTicket);
          startExportRefresh(newTicket);
          setIsShowingTicketForm(false);
          toasterStore.push(alert(t('approvals.ticket_created'), AlertTypes.success));
        }
      } catch (error) {
        connection.handleRequestError(
          error,
          ticket?.id
            ? t('error_messages.tickets.failed_to_update')
            : t('error_messages.tickets.failed_to_create'),
        );
      } finally {
        setBusyReason(undefined);
      }
    }
  }, [
    effectiveCompanyId,
    ticket,
    load?.jobId,
    load?.id,
    onLoadUpdated,
    startExportRefresh,
  ]);

  const handleTicketImageStateChange = useCallback((isManipulatinImage: boolean) => {
    const newBusyReason = isManipulatinImage
      ? TicketDrawerBusyReason.MANIPULATING_IMAGE
      : undefined;

    setBusyReason(newBusyReason);
  }, []);

  const handleApproveLoad = useCallback(
    async (isApproveAndNext?: boolean) => {
      if (!load?.id) {
        return;
      }

      try {
        setBusyReason(TicketDrawerBusyReason.APPROVING_LOAD);

        const response = await putV1CompaniesCompanyIdLoadsLoadIdApprove({
          path: { 'company-id': companyId, 'load-id': load.id },
        });
        const updatedLoad = Load.parse(response.data.data);
        trackLoadsApproved([updatedLoad]);
        setLoad(updatedLoad);
        refreshLoads();
        toasterStore.push(
          alert(t('ticket_drawer_v2.success_messages.load_approved'), AlertTypes.success),
        );

        setBusyReason(undefined);

        if (isApproveAndNext) {
          onShowNextTicketClick?.();
        }
      } catch (error) {
        setBusyReason(undefined);
        connection.handleRequestError(
          error,
          t('error_messages.jobs.failed_to_approve_load'),
        );
      }
    },
    [companyId, load?.id, onShowNextTicketClick, refreshLoads],
  );

  const handleUnapproveLoad = useCallback(async () => {
    if (!load?.id) {
      return;
    }

    try {
      setBusyReason(TicketDrawerBusyReason.UNAPPROVING_LOAD);

      const response = await putV1CompaniesCompanyIdLoadsLoadIdUnapprove({
        path: { 'company-id': companyId, 'load-id': load.id },
      });
      const updatedLoad = Load.parse(response.data.data);
      setLoad(updatedLoad);
      refreshLoads();
      toasterStore.push(
        alert(t('ticket_drawer_v2.success_messages.load_unapproved'), AlertTypes.success),
      );

      setBusyReason(undefined);
    } catch (error) {
      setBusyReason(undefined);
      connection.handleRequestError(
        error,
        t('error_messages.jobs.failed_to_approve_load'),
      );
    }
  }, [companyId, load?.id, refreshLoads]);

  const handleDismissAiReview = useCallback(async () => {
    const aiReviewId = load?.aiReview?.id;

    if (!load || !aiReviewId) {
      return;
    }

    try {
      setBusyReason(TicketDrawerBusyReason.DISMISSING_AI_REVIEW);

      const dismissedReview = await dismissAiReview({ aiReviewId, companyId });

      if (dismissedReview) {
        trackAiReviewRejected(load.id);
      }

      refreshLoads();
    } catch {
      // Already reported by the dismiss call. Contained here because the link invokes
      // this handler without awaiting it.
    } finally {
      setBusyReason(undefined);
    }
  }, [companyId, dismissAiReview, load, refreshLoads]);

  const handleUndoAiReviewDismissal = useCallback(async () => {
    const aiReviewId = load?.aiReview?.id;

    if (!load || !aiReviewId) {
      return;
    }

    try {
      setBusyReason(TicketDrawerBusyReason.UNDOING_AI_REVIEW_DISMISSAL);

      // The endpoint answers with the review, not its load, so the drawer takes the
      // load's new state from the refresh the parent feeds back in through props.
      await undismissAiReview({ aiReviewId, companyId });

      refreshLoads();
    } catch {
      // Already reported by the undismiss call. Contained here because the undo link
      // invokes this handler without awaiting it.
    } finally {
      setBusyReason(undefined);
    }
  }, [companyId, load?.aiReview?.id, refreshLoads, undismissAiReview]);

  const handleTicketPotentialDuplicateChange = useCallback(
    async (potentialDuplicate: boolean) => {
      if (!ticket?.id) {
        return;
      }

      try {
        setBusyReason(TicketDrawerBusyReason.CHANGING_TICKET_POTENTIAL_DUPLICATE_STATE);

        const updatedTicket = await updateTicketPotentialDuplicate({
          companyId: effectiveCompanyId,
          id: ticket.id,
          potentialDuplicate,
        });

        setTicket(updatedTicket);
        refreshTicket(updatedTicket);
      } catch {
        // Already reported by updateTicketPotentialDuplicate. The buttons call this
        // handler without awaiting it.
      } finally {
        setBusyReason(undefined);
      }
    },
    [effectiveCompanyId, ticket?.id, refreshTicket, updateTicketPotentialDuplicate],
  );

  const handleTicketFlaggedStatusChange = useCallback(async () => {
    if (!ticket?.id) {
      return;
    }

    try {
      setBusyReason(TicketDrawerBusyReason.CHANGING_TICKET_FLAGGED_STATE);

      const response = await patchV1CompaniesCompanyIdTicketsId({
        path: { 'company-id': effectiveCompanyId, id: ticket.id },
        body: { flagged: !ticket.flagged },
      });
      const updatedTicket = Ticket.parse(response.data.data);

      setTicket(updatedTicket);
      toasterStore.push(alert(t('approvals.ticket_updated'), AlertTypes.success));
    } catch (error) {
      connection.handleRequestError(error, t('error_messages.tickets.failed_to_update'));
    } finally {
      setBusyReason(undefined);
    }
  }, [effectiveCompanyId, ticket?.id, ticket?.flagged]);

  const fetchLoadSignatures = (loadId: string) =>
    fetchAllPages((cursor, limit) => getSignaturesByLoadId({ cursor, limit, loadId }));

  useEffect(() => {
    setTicket(initialTicket);
  }, [initialTicket]);

  useEffect(() => {
    setLoad(initialLoad);
  }, [initialLoad]);

  useEffect(() => {
    setIsShowingTicketForm(!initialTicket?.id);
  }, [initialLoad?.id, initialTicket?.id]);

  useEffect(() => {
    const orderId = job.order?.id;
    orderRequestRef.current = orderId ?? null;
    setOrder(undefined);

    const fetchOrder = async () => {
      if (!orderId) return;

      try {
        const fullOrder = await getOrderById(orderId);
        if (orderRequestRef.current === orderId) {
          setOrder(fullOrder);
        }
      } catch {
        // Error handled by useOrders.getOrderById
      }
    };

    fetchOrder();
  }, [job.order?.id]);

  useEffect(() => {
    if (ticket?.id) {
      setIsShowingTicketForm(false);
    }
  }, [ticket?.id]);

  useEffect(() => {
    const fetchSignatures = async () => {
      setSignatures([]);

      if (load?.id) {
        try {
          const signatures = await fetchLoadSignatures(load.id);
          setSignatures(signatures);
        } catch {
          // Error handled by useJob.getSignaturesByLoadId; swallow so this
          // fire-and-forget effect does not raise an unhandled rejection when a
          // load is deleted out from under an open drawer. See HORIZON-FE-2MM.
        }
      }
    };

    fetchSignatures();
  }, [load?.id]);

  useEffect(() => {
    if (ticket?.id && isAnalyzingTicket) {
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }

      // 30-second timeout to clear analyzing state and show error
      analysisTimeoutRef.current = setTimeout(() => {
        ticketsStore.ticketAnalysisEnd(ticket.id);
        toasterStore.push(
          alert(
            t('ticket_drawer_v2.image_uploader.scanning_ticket_image_timeout_message'),
            AlertTypes.error,
          ),
        );
      }, 30000);
    }
    return () => {
      if (analysisTimeoutRef.current) {
        clearTimeout(analysisTimeoutRef.current);
      }
    };
  }, [ticket?.id, isAnalyzingTicket, ticketsStore]);

  useEffect(() => {
    if (ticket?.id && isAnalyzingTicket) {
      const storeTicket = ticketsStore.tickets.find((t) => t.id === ticket.id);
      if (storeTicket && storeTicket.aiProcessed) {
        // RTU came in with aiProcessed: true, clear timeout and end analyzing state
        if (analysisTimeoutRef.current) {
          clearTimeout(analysisTimeoutRef.current);
        }
        ticketsStore.ticketAnalysisEnd(ticket.id);
        setTicket(storeTicket);
        refreshTicket(storeTicket);
      }
    }
  }, [ticket?.id, isAnalyzingTicket, ticketsStore.tickets, refreshTicket]);

  useEffect(() => {
    const fetchTicketMetadata = async () => {
      setTicketMetadata(undefined);
      setIsLoadingTicketMetadata(false);

      if (ticket?.id && ticket.aiProcessed) {
        const currentTicketId = ticket.id;
        ticketMetadataRequestRef.current = currentTicketId;
        setIsLoadingTicketMetadata(true);

        try {
          const data = await getTicketMetadataById({
            companyId: effectiveCompanyId,
            id: currentTicketId,
          });

          if (ticketMetadataRequestRef.current === currentTicketId) {
            setTicketMetadata(data);
          }
        } catch {
          // Request errors are handled by useTickets.getTicketMetadataById.
        } finally {
          if (ticketMetadataRequestRef.current === currentTicketId) {
            setIsLoadingTicketMetadata(false);
            ticketMetadataRequestRef.current = null;
          }
        }
      } else {
        ticketMetadataRequestRef.current = null;
      }
    };

    fetchTicketMetadata();

    return () => {
      ticketMetadataRequestRef.current = null;
    };
  }, [effectiveCompanyId, ticket?.id, ticket?.aiProcessed]);

  return (
    <>
      <Drawer
        anchor="right"
        onClose={handleGuardedClose}
        open={isOpen}
        variant="temporary"
        sx={{
          [`& .${drawerClasses.paper}`]: {
            height: 'calc(100dvh - var(--tread-agent-dock-height, 0px))',
            maxWidth: '1200px',
            overflow: 'hidden',
            width: '80%',
          },
        }}
      >
        <Box
          sx={{
            display: 'grid',
            gridTemplateAreas:
              '"header header header" "export export export" "attachments divider ctas" "attachments divider details" "attachments divider footer"',
            gridTemplateColumns: '1fr auto 1fr',
            // ctas row is auto-height (collapses when empty); footer collapses to
            // 0 in edit mode since Cancel/Save move into the action bar.
            gridTemplateRows: `70px auto auto 1fr ${isTicketFormVisible ? '0px' : '62px'}`,
            height: '100%',
          }}
        >
          <DrawerHeader
            job={job}
            load={load}
            loadNumber={loadNumber}
            nextUnavailableReason={busyReason ? undefined : nextUnavailableReason}
            onClose={performClose}
            onNextClick={
              busyReason || !onShowNextTicketClick ? undefined : handleShowNextTicket
            }
            onPreviousClick={
              busyReason || !onShowPreviousTicketClick
                ? undefined
                : handleShowPreviousTicket
            }
            previousUnavailableReason={busyReason ? undefined : previousUnavailableReason}
            sx={{ gridArea: 'header' }}
          />

          {!load && emptyStateMessage && (
            <>
              <Box sx={{ gridArea: 'attachments', overflow: 'auto' }}>
                <TicketDrawerEmptyState
                  description={emptyStateMessage}
                  title={t('ticket_drawer_v2.empty_job_title')}
                />
              </Box>

              {onShowNextTicketClick && (
                <Box
                  sx={(theme) => ({
                    gridArea: 'ctas',
                    bgcolor: 'background.paper',
                    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    display: 'flex',
                    justifyContent: 'flex-end',
                    px: 2,
                    py: 1.5,
                  })}
                >
                  <SmallButton
                    color="brandV2Black"
                    onClick={handleShowNextTicket}
                    variant="outlined"
                    sx={{ color: 'text.primary', borderColor: 'divider' }}
                  >
                    {t('ticket_drawer_v2.next_load')}
                  </SmallButton>
                </Box>
              )}

              <Box sx={{ gridArea: 'details', overflow: 'auto', p: 2 }}>
                <JobSummaryPanel job={job} />
              </Box>

              <DrawerFooter onCloseClick={performClose} sx={{ gridArea: 'footer' }} />
            </>
          )}

          {load && !isNil(loadNumber) && (
            <>
              {isLoadCanceled && !isShowingTicketForm && (
                <Alert
                  severity="info"
                  data-test-id="ticket-drawer-canceled-load-banner"
                  sx={{ gridArea: 'ctas', mx: 2, mt: 1 }}
                >
                  {t('ticket_drawer_v2.canceled_load_banner')}
                </Alert>
              )}

              {ticket && (
                <TicketExportBlock
                  ticket={ticket}
                  sx={(theme) => ({
                    borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                    gridArea: 'export',
                  })}
                />
              )}

              <Box sx={{ gridArea: 'attachments', overflow: 'auto' }}>
                <TicketAttachments
                  key={ticket?.id}
                  busyReason={busyReason}
                  isTicketImageEditable={isTicketEditable || canCreateTicket}
                  load={load}
                  onDeleteTicketImage={handleDeleteTicketImage}
                  onTicketImageStateChange={handleTicketImageStateChange}
                  refreshTicket={refreshTicket}
                  ticket={ticket}
                />
                <Box sx={{ px: 2, pb: 2 }}>
                  <FileAttachments
                    fileAttachableId={load.id}
                    fileAttachableType={FileAttachableType.LOAD}
                    control={fileAttachmentsControl}
                    errors={fileAttachmentsErrors}
                    form={fileAttachmentsForm}
                    name="fileAttachments"
                    allowPerItemUpdates
                  />
                </Box>
              </Box>

              <Box sx={{ gridArea: 'divider' }}>
                <Divider orientation="vertical" />
              </Box>

              <TicketDetailsCTAs
                busyReason={busyReason}
                isEditingTicket={isShowingTicketForm}
                isTickedFlagged={Boolean(ticket?.flagged)}
                onApproveLoadAndNextClick={
                  availableCTAs.includes('approve-load-and-next')
                    ? () => handleApproveLoad(true)
                    : undefined
                }
                onApproveLoadClick={
                  availableCTAs.includes('approve-load')
                    ? () => handleApproveLoad()
                    : undefined
                }
                onCancelClick={handleCancelEdit}
                onEditClick={
                  !isShowingTicketForm && availableCTAs.includes('edit-ticket')
                    ? () => setIsShowingTicketForm(true)
                    : undefined
                }
                onSaveClick={handleSaveTicket}
                onTickedFlaggedStatusChangeClick={
                  availableCTAs.includes('flag-ticket') ||
                  availableCTAs.includes('unflag-ticket')
                    ? handleTicketFlaggedStatusChange
                    : undefined
                }
                onUnapproveLoadClick={
                  availableCTAs.includes('unapprove-load')
                    ? handleUnapproveLoad
                    : undefined
                }
                sx={(theme) => ({
                  gridArea: 'ctas',
                  bgcolor: 'background.paper',
                  borderBottom: `solid 1px ${theme.brandV2.colors.treadGray7}`,
                  px: 2,
                  py: 1.5,
                })}
              />

              <Box
                sx={{
                  gridArea: 'details',
                  overflow: 'auto',
                  p: 2,
                }}
              >
                <FlexColumn sx={{ gap: 2 }}>
                  <LoadApprovals
                    key={load.id}
                    companyId={companyId}
                    load={load}
                    isOrderEditable={Boolean(job.order?.editable)}
                  />
                  {load.aiReview && !hidesAiReviewSummary && (
                    <AiReviewSummary
                      aiReview={load.aiReview}
                      isDismissing={
                        busyReason === TicketDrawerBusyReason.DISMISSING_AI_REVIEW
                      }
                      isUndoingDismiss={
                        busyReason === TicketDrawerBusyReason.UNDOING_AI_REVIEW_DISMISSAL
                      }
                      onDismiss={canDismissAiReview ? handleDismissAiReview : undefined}
                      onUndoDismiss={
                        canDismissAiReview ? handleUndoAiReviewDismissal : undefined
                      }
                    />
                  )}
                  {isShowingTicketForm ? (
                    <TicketForm
                      key={`${load.id}-${ticket?.id ?? 'new-ticket'}`}
                      ref={ticketFormRef}
                      job={job}
                      load={load}
                      order={order}
                      ticket={ticket}
                    />
                  ) : (
                    <DigitalTicketView
                      isAnalyzingTicket={isAnalyzingTicket}
                      isLoadingMetadata={isLoadingTicketMetadata}
                      job={job}
                      load={load}
                      metadata={ticketMetadata}
                      onAddNewTicketClick={
                        canCreateTicket ? () => setIsShowingTicketForm(true) : undefined
                      }
                      isChangingPotentialDuplicate={
                        busyReason ===
                        TicketDrawerBusyReason.CHANGING_TICKET_POTENTIAL_DUPLICATE_STATE
                      }
                      onPotentialDuplicateChange={
                        userPermissions.canEditTicketPotentialDuplicate
                          ? handleTicketPotentialDuplicateChange
                          : undefined
                      }
                      order={order}
                      ticket={ticket}
                      signatures={signatures}
                    />
                  )}
                </FlexColumn>
              </Box>

              {!isShowingTicketForm && (
                <DrawerFooter onCloseClick={performClose} sx={{ gridArea: 'footer' }} />
              )}
            </>
          )}
        </Box>
      </Drawer>

      <ConfirmationDialog
        cancelButtonProps={{ children: t('actions.no') }}
        confirmButtonProps={{ children: t('actions.exit'), color: 'primary' }}
        isOpen={isConfirmingDiscard}
        onClose={cancelDiscard}
        onConfirm={confirmDiscard}
        title={t('ticket_drawer_v2.confirm_exit.title')}
      >
        <Typography>{t('ticket_drawer_v2.confirm_exit.text')}</Typography>
      </ConfirmationDialog>
    </>
  );
}

export default observer(TicketDrawer);
